import { CropStatusEnum, ROUTE_LOADS } from '@/common/constants';
import { ContractTypesEnum } from '@/common/constants/contract.types.constants';
import { PartiesEnum } from '@/common/constants/parties.constants';
import { ComboboxControl } from '@/components/controls/combobox-control';
import { InputControl } from '@/components/controls/input-control';
import SelectControl from '@/components/controls/select-control';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Form } from '@/components/ui/form';
import { Label } from '@/components/ui/label';
import { usePartyStore } from '@/features/parties/store/usePartyStore';
import useStore from '@/lib/store';
import { cn } from '@/lib/utils';
import { yupResolver } from '@hookform/resolvers/yup';
import { FC, useEffect, useMemo, useState } from 'react';
import { SubmitHandler, useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { PrintDocsModal } from './GenerateDocsModal';
import { generatePDF } from './generateFile';
import { ILandMergeData, ILoadGeneral } from './models';
import { GeneralSchema } from './schemas';
import useMeasureStore from './store/useMeasureStore';

export const GeneralForm: FC = () => {
  const { user } = useStore();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [showCottonTypes, setShowCottonTypes] = useState(false);

  const [openModal, setOpenModal] = useState(false);
  const [contractList, setContractList] = useState<any[]>();

  const [load, setLoad] = useState<any>();
  const [cropStatus, setCropStatus] = useState(
    CropStatusEnum.HAS_LAND_MERGE_DATA,
  );

  const [dealType, setDealType] = useState(undefined);
  const [mergedTypeList, setMergedTypeList] = useState<any>([]);

  const form = useForm<ILoadGeneral | any>({
    resolver: yupResolver(GeneralSchema),
    context: { cropStatus, dealType },
    defaultValues: {
      tax_number: '',
    },
  });

  const {
    fetchFarmer,
    saveReception,
    harvestType,
    receptionLoading,
    improvedCottonStandarts,
  } = useMeasureStore();
  const { cropTypes, cropGenerations, generationTypes, storageTypes } =
    usePartyStore();
  const { control, setValue, watch, formState, handleSubmit } = form;

  const crop_generation_type = useWatch({
    control: form.control,
    name: 'crop_generation_type',
  });

  const getFarmer = async () => {
    const inn = form.getValues('tax_number');
    form.reset({
      tax_number: inn,
      farmer_contract_id: '',
      crop_data: '',
      farmer: '',
      buyer_tin: '',
      area: '',
      harvest_type: ';',
    });

    try {
      const farmer = await fetchFarmer(inn);
      setValue('farmer', farmer.name);

      setContractList(
        farmer.contracts?.map((el) => ({
          label:
            el?.deal_number == null
              ? `${el?.contract_number} / "${t(el.deal_type)}" - ${el.buyer_name}`
              : `${el?.deal_number} / "${t(el.deal_type)}" - ${el.buyer_name}`,
          value: el.id,
          ...el,
        })),
      );
    } catch (error) {
      console.error('Failed to fetch farmer:', error);
    }
  };

  useEffect(() => {
    if (watch('tax_number').length === 9) {
      getFarmer();
    }
  }, [watch('tax_number').length === 9]);

  useEffect(() => {
    if (!watch('farmer_contract_id')) return;
    const contract = contractList?.find(
      (el) => el.value === watch('farmer_contract_id'),
    );

    setValue('crop_data', '');
    setValue('crop_generation_id', '');
    setValue('crop_type_id', '');

    setValue('contract_amount', contract?.harvest_plan ?? undefined);
    setValue('reception_weight', contract?.reception_weight ?? undefined);
    if (contract?.harvest_plan && contract?.reception_weight) {
      setValue(
        'residual_amount',
        +contract?.harvest_plan - +contract?.reception_weight,
      );
    }

    setValue('buyer_tin', contract?.buyer_tin);
    setValue('buyer_name', contract?.buyer_name);
    setValue('area', contract?.area);

    if (contract?.land_merge_data?.length === 1) {
      // if contract has one land_merge_data

      const cropType = cropTypes?.find(
        (type) =>
          type.value == String(contract.land_merge_data?.at(0)?.crop_type_id),
      );
      const cropGeneration = cropGenerations?.find(
        (gen) =>
          gen.value == contract.land_merge_data?.at(0)?.crop_generation_id,
      );

      if (cropType && cropGeneration) {
        // if data has type and generation

        setMergedTypeList([
          {
            value: `${cropType.value},${cropGeneration.value}`,
            label: `${cropType?.label} / ${cropGeneration?.label}`,
          },
        ]);
        setCropStatus(CropStatusEnum.HAS_LAND_MERGE_DATA);
      } else if (
        (cropGeneration && !cropType) ||
        (cropType && !cropGeneration)
      ) {
        // sometimes cropType or generationType cannot find

        setCropStatus(CropStatusEnum.NOT_HAS_LAND_MERGE_DATA);
      }
    } else if (contract?.land_merge_data?.length > 1) {
      // land_merge_data can has more than 1 contracts

      const arr: any = [];
      contract?.land_merge_data.forEach((el: ILandMergeData) => {
        const cropType = cropTypes?.find(
          (type) => type.value == String(el?.crop_type_id),
        );
        const cropGeneration = cropGenerations?.find(
          (gen) => gen.value == el?.crop_generation_id,
        );

        if (cropType && cropGeneration) {
          arr.push({
            value: `${cropType.value},${cropGeneration.value}`,
            label: `${cropType?.label} / ${cropGeneration?.label}`,
          });
        }
      });

      if (arr.length > 0) {
        setMergedTypeList(arr);
        setCropStatus(CropStatusEnum.HAS_LAND_MERGE_DATA);
      }
    } else {
      setCropStatus(CropStatusEnum.NOT_HAS_LAND_MERGE_DATA);
    }
  }, [watch('farmer_contract_id')]);

  const contract = useMemo(() => {
    setDealType(
      contractList?.find((el) => el.id === watch('farmer_contract_id')),
    );
    return contractList?.find((el) => el.id === watch('farmer_contract_id'));
  }, [watch('farmer_contract_id')]);

  useEffect(() => {
    if (!watch('crop_data')) return;
    setValue('crop_type_id', watch('crop_data')?.split(',')[0]);
    setValue('crop_generation_id', watch('crop_data')?.split(',')[1]);
  }, [watch('crop_data')]);

  const generateDocs = async () => {
    await generatePDF(
      load!,
      user!,
      load?.number,
      load?.improved_cotton,
      load?.created_at,
      load,
    );
    navigate(`${ROUTE_LOADS}`);
  };

  useEffect(() => {
    if (
      cropStatus === CropStatusEnum.NOT_HAS_LAND_MERGE_DATA &&
      crop_generation_type === PartiesEnum.TECHNICAL
    ) {
      form.setValue('crop_generation_id', '');
    }
  }, [crop_generation_type]);

  const saveForm: SubmitHandler<ILoadGeneral | any> = async (data) => {
    const payload = {
      area: data?.area,
      buyer_tin: data?.buyer_tin,
      farmer_contract_id: data?.farmer_contract_id,
      crop_generation_id: data?.crop_generation_id,
      tax_number: data?.tax_number,
      crop_type_id: data?.crop_type_id,
      harvest_type: data?.harvest_type,
      crop_generation_type: data?.crop_generation_type,
      improved_cotton: data?.improved_cotton,
      storage_type: data?.storage_type,
    };

    if (data?.crop_generation_id === '') {
      delete payload.crop_generation_id;
    }

    if (data?.storage_type === '') {
      delete payload.storage_type;
    }

    if (data?.improved_cotton === '') {
      delete payload.improved_cotton;
    }

    try {
      const result = await saveReception(payload);
      setLoad(result);
      setOpenModal(true);
    } catch (error) {
      console.error('Receptionni saqlashda xatolik:', error);
    }
  };

  const filteredGenerationTypes = useMemo(() => {
    if (contract) {
      if (!generationTypes) return [];
      if (contract?.deal_type !== ContractTypesEnum.STORAGE) {
        if (contract?.is_seed) {
          form.setValue(
            'crop_generation_type',
            generationTypes?.find((v) => v?.value !== 'TECHNICAL')?.value,
          );
        } else {
          form.setValue(
            'crop_generation_type',
            generationTypes?.find((v) => v?.value !== 'SEED')?.value,
          );
        }
        return contract?.is_seed
          ? generationTypes?.filter((v) => v?.value !== 'TECHNICAL')
          : generationTypes?.filter((v) => v?.value !== 'SEED');
      } else return generationTypes;
    }
  }, [contract, generationTypes]);

  return (
    <Form {...form}>
      <form
        onSubmit={handleSubmit(saveForm)}
        className={cn('flex flex-col items-end space-y-5')}
      >
        <div className="grid grid-cols-12 items-end gap-4 w-full">
          <InputControl
            control={control}
            name="tax_number"
            label={t('stir')}
            placeholder={t('enter.stir')}
            className="col-span-4"
            maxLength={9}
            isNumber
          />

          <InputControl
            control={control}
            name="farmer"
            label={t('name.product.delivery')}
            placeholder="---"
            className="col-span-8"
            disabled
          />
          <SelectControl
            name="farmer_contract_id"
            label={t('contract')}
            options={contractList}
            control={control}
            placeholder={t('choose.data')}
            className={cn('col-span-6')}
            disabledItem={(option) => !option?.can_use}
          />

          <InputControl
            control={control}
            name="buyer_name"
            label={t('organization.name.two')}
            placeholder="---"
            className={cn('col-span-6')}
            disabled
          />

          {watch('contract_amount') !== undefined && (
            <div className="col-span-12 grid grid-cols-12 gap-4">
              <InputControl
                control={control}
                name="buyer_tin"
                label={t('organization.stir.number')}
                placeholder="---"
                className="col-span-3"
                disabled
              />
              <InputControl
                control={control}
                name="contract_amount"
                label={t('contract.amount')}
                placeholder="---"
                className="col-span-3"
                disabled
              />
              <InputControl
                control={control}
                name="reception_weight"
                label={t('reception.amount')}
                placeholder="---"
                className="col-span-3"
                disabled
              />
              <InputControl
                control={control}
                name="residual_amount"
                label={t('residual.amount')}
                placeholder="---"
                className="col-span-3"
                disabled
              />
            </div>
          )}
          {cropStatus === CropStatusEnum.HAS_LAND_MERGE_DATA ? (
            <>
              <SelectControl
                name="crop_data"
                label={t('selection.type.generation')}
                options={mergedTypeList}
                control={control}
                placeholder={t('choose.data')}
                className="col-span-3"
              />
              {watch('farmer_contract_id') && (
                <Button
                  className="col-span-1"
                  type="button"
                  onClick={() =>
                    setCropStatus(CropStatusEnum.NOT_HAS_LAND_MERGE_DATA)
                  }
                >
                  O'zgartirish
                </Button>
              )}
              <SelectControl
                name="crop_generation_type"
                label={t('generation.type')}
                options={filteredGenerationTypes}
                control={control}
                placeholder={t('choose.data')}
                className="col-span-4"
                disabled={contract?.deal_type !== ContractTypesEnum.STORAGE}
              />
            </>
          ) : (
            <>
              <ComboboxControl
                name="crop_type_id"
                label={t('selection.type')}
                options={cropTypes}
                control={control}
                placeholder={t('choose.data')}
                className="w-full col-span-4"
              />
              <SelectControl
                name="crop_generation_type"
                label={t('generation.type')}
                options={filteredGenerationTypes}
                control={control}
                placeholder={t('choose.data')}
                disabled={contract?.deal_type !== ContractTypesEnum.STORAGE}
                className="col-span-4"
              />
              {crop_generation_type === PartiesEnum.SEED && (
                <>
                  <SelectControl
                    name="crop_generation_id"
                    label={t('selection.type.clan')}
                    options={cropGenerations}
                    control={control}
                    placeholder={t('choose.data')}
                    className="col-span-4"
                    disabled={!crop_generation_type}
                  />
                  <SelectControl
                    name="storage_type"
                    label={t('storage.type')}
                    options={storageTypes}
                    control={control}
                    placeholder={t('choose.data')}
                    className="col-span-4"
                  />
                </>
              )}
            </>
          )}
          <InputControl
            control={control}
            name="area"
            label={t('cultivated.area')}
            placeholder="---"
            className="col-span-4"
            disabled
          />
          <SelectControl
            name="harvest_type"
            label={t('harvest.type')}
            options={harvestType}
            control={control}
            placeholder={t('choose.data')}
            className="col-span-4"
            disableSort
          />
          <div className="flex items-end gap-3 col-span-2">
            <Checkbox
              id="terms"
              checked={showCottonTypes}
              onCheckedChange={(checked) => setShowCottonTypes(!!checked)}
            />
            <Label
              htmlFor="terms"
              className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
            >
              {t('high.quality.cotton')}
            </Label>
          </div>
          {showCottonTypes && (
            <SelectControl
              name="improved_cotton"
              label="Paxta"
              options={improvedCottonStandarts}
              control={control}
              placeholder={t('choose.data')}
              className="col-span-4"
              disableSort
            />
          )}
        </div>
        <div className="flex-h-center space-x-4">
          {user && (
            <Button
              type="submit"
              disabled={!formState.isValid || receptionLoading}
              isLoading={receptionLoading}
            >
              {t('save')}
            </Button>
          )}
        </div>
        <PrintDocsModal
          open={openModal}
          contract={contract}
          setOpen={setOpenModal}
          printDocs={generateDocs}
          organization={user}
          id={load?.number}
          improved_cotton={load?.improved_cotton}
          created_at={load?.created_at}
          load={load}
        ></PrintDocsModal>
      </form>
    </Form>
  );
};
